import clsx from 'clsx';
import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import config from '../../app/config';
import moment from 'moment';
import {TimerCountUp} from '../../controls/agyle/timer';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import AgyleButton from '../ui/buttons/agyleButton';
import AddIcon from '@mui/icons-material/Add';
import {useTranslation} from 'react-i18next';
import {Box} from '@mui/material';
import {
    MainButtonBackground,
    patientSafetyCheckListLastSectionValid,
    patientSafetyChecklistSectionValidation,
    SEPSIS_TOOL_TYPE,
} from '../../pages/ward/forms/formsUtil';
import {useMainScreenButtonStyles} from './mainScreenButtonStyles';
import {ctxShowMandatoryAssessment, openPatientForm, setForceSelectLastForm} from '../../store/reducers/patientContext';
import {useDispatch, useSelector} from 'react-redux';
import AgyleLoadingSpinner from '../other/agyleLoadingSpinner';
import HistoryFormButton from './historyFormButton';
import {assessmentsConfig} from '../../store/reducers/externalConfig';
import {DEFAULT_ASSESSMENTS_VALUE} from '../../pages/admin/aglyeConfig/configDefaultValues';
import {ASSESSMENT_MANDATORY_CONFIG} from '../../util/patientContextUtil';
import {getConsultantName} from '../../controls/agyle/clinician/clinitianUtils';

const MainScreenButton = (props) => {
    const classes = useMainScreenButtonStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {admitButton, isMultiple, filled, isAssessment} = props;

    // Selectors
    const assCfg = useSelector(assessmentsConfig);
    const showMandatoryAssessmentOnDischarge = useSelector(ctxShowMandatoryAssessment);

    const [isLoadingIsHistory, setLoadingIsHistory] = useState(isMultiple);
    const [showMandatoryBorder, setShowMandatoryBorder] = useState(false);

    useEffect(() => {
        const mandatory = assCfg?.mandatory || DEFAULT_ASSESSMENTS_VALUE.mandatory;
        if (isAssessment && mandatory?.enabled) {
            let showRedBorder = mandatory.showBorderline === ASSESSMENT_MANDATORY_CONFIG.ALWAYS_SHOW_BORDERLINE && !filled;
            if (!showRedBorder && !filled && showMandatoryAssessmentOnDischarge) showRedBorder = true;
            setShowMandatoryBorder(showRedBorder);
        }
    }, [filled, assCfg, showMandatoryAssessmentOnDischarge]);

    const getUser = () => {
        if (props.customData?.by) return props.customData.by;

        const path = props.userPath || 'who';
        const obj = _.get(props.value, path);

        if (_.isObject(obj)) return getConsultantName(obj);
        if (_.isString(obj)) return obj;

        return '-';
    };

    const getCountdown = () => {
        const countdownTimestamp = props.countdown();
        const nowUnix = moment().format('x');
        const countdownUnix = moment(countdownTimestamp).format('x');
        const isCountdownGreaterThanHour = countdownUnix - nowUnix > 3600000;

        const psc = props.patient?.studies?.safetyChecklist;
        const validSection1 = patientSafetyChecklistSectionValidation(psc, 1, props.patient?.patient);
        const validSection2 = validSection1 && patientSafetyChecklistSectionValidation(psc, 2);
        const validSection3 = validSection2 && patientSafetyChecklistSectionValidation(psc, 3);
        const validSection4 = validSection3 && patientSafetyChecklistSectionValidation(psc, 4);

        if (validSection4 || isCountdownGreaterThanHour) {
            return;
        }

        if (nowUnix > countdownUnix) {
            return <div>00:00</div>;
        }

        return <TimerCountUp
            time={countdownTimestamp}
            withoutHours
            limitMessage="0:00:00"
            countDown
        />;
    };

    const loadFullForm = () => {
        if (props.openFormPath === 'sepsisTool' && props.customData) {
            if (props.customData?.status === SEPSIS_TOOL_TYPE.IN_COMPLETED) {
                dispatch(setForceSelectLastForm(props.customData));
            }
        }
        dispatch(openPatientForm(props.openFormPath));
    };

    const handleOpenForm = (e) => {
        const disableSelect = e.target.closest('.disabled-options') !== null;
        if (!props.disabled && !disableSelect) {
            if (props.onOpen) props.onOpen();
            else if (props.openFormPath) loadFullForm();
        }
    };

    const getDate = () => {
        if (props.customData) return moment(props.customData?.when).format(config.defaultDateTimeFormat);

        const path = props.datePath ? props.datePath : 'when';
        const obj = _.get(props.value, path);
        if (obj) {
            return moment(obj).format(config.defaultDateTimeFormat);
        }
        return '-';
    };

    const isTimeStamp = () => {
        return props.value && props.value.status && props.value.status === 'timestamp';
    };

    const isCustomBackground = () => props.customColor || (props?.patient && !patientSafetyCheckListLastSectionValid(props.patient?.studies?.safetyChecklist, props.patient?.patient));

    const renderCtx = () => {
        if (props?.spacialRender && props?.spacialRender()) {
            return (
                <div
                    className={clsx(classes.formFilledDetails, 'label-set')}
                    style={{
                        backgroundColor: props?.filledTextContainerBackground || '',
                        color: props?.filledTextContainerFontColor || '',
                    }}
                >
                    {props.spacialRender()}
                </div>
            );
        } else if (props?.filled) {
            return (
                <div className={clsx(classes.formFilledDetails, 'label-set')}>
                    {!isTimeStamp() &&
                        <Typography
                            variant="subtitle1"
                            sx={{pl: !props.filled ? 0.5 : 1.2}}
                            fontWeight="normal"
                            component={'span'}
                        >
                            {props?.extraLine ? props?.extraLine() : <br/>}
                        </Typography>
                    }

                    {config?.patientSafetyChecklist?.enabled && props.countdown &&
                        <Typography variant="subtitle1" component={'span'}>
                            <div className="label-part2">
                                {getCountdown()}
                            </div>
                        </Typography>
                    }

                    {isTimeStamp() &&
                        <Typography variant="subtitle1">
                            <div className="label-part2">
                                Time Stamp:
                            </div>
                        </Typography>
                    }

                    <div style={{display: 'flex', minWidth: '100%'}}
                         className={clsx(classes.seenDlg, 'filledBackground')}
                    >
                        <Typography variant="subtitle1" component={'span'}>
                            <div className="label-part2">
                                {getUser()}
                            </div>
                        </Typography>

                        <Typography variant="subtitle1" component={'span'}>
                            <span className="label-part3">
                                {getDate()}
                            </span>
                        </Typography>
                    </div>
                </div>
            );
        } else if (props?.notSet) {
            return (
                <div className={clsx(classes.formFilledDetails, 'label-set')} style={{display: 'flex'}}>
                    <div className="label-part2" style={{paddingLeft: 4}}>
                        {props.notSetText || 'Not Set'}
                    </div>
                </div>
            );
        }
        return null;
    };

    if (props?.buttonOnly) {
        return (
            <AgyleButton
                width={props.noWidth ? null : '100%'}
                variant="contained"
                color={props.color}
                onClick={!props.disabled ? props.onOpen : null}
                startIcon={!props?.filled ? <AddIcon/> : null}
                disabled={props.disabled}
                style={{
                    borderRadius: 5,
                    marginLeft: 0,
                    backgroundColor: props?.filledTextContainerBackground || undefined,
                    color: props?.filledTextContainerFontColor || undefined,
                    border: props?.filledTextContainerBorder || undefined,
                }}
            >
                {props.buttonOnlyLabel}
            </AgyleButton>
        );
    }

    const getButtonBackground = () => {
        if (showMandatoryBorder) return MainButtonBackground.RED;
        return isCustomBackground() && props?.btnBackground ? props?.btnBackground() : '';
    };

    return (
        <Grid
            container
            onClick={handleOpenForm}
            className={clsx(classes.background, (props.disabled || props.disableEdit) && 'disabled')}
            style={{
                background: getButtonBackground(),
                cursor: (props.disabled || props.disableEdit) ? 'default' : 'pointer',
                height: '100px',
                padding: '1.5px',
            }}
        >
            <Grid
                item
                xs={12}
                className={clsx(
                    classes.buttonHeader,
                    (props.filled || (props.notSet) || isTimeStamp()) && classes.buttonHeaderFilled,
                )}
            >
                <Box fontWeight="bolder" sx={{pl: !props.filled ? 1.5 : 'auto', pt: 0.35}}>
                    {props.label}
                </Box>

                <div className={classes.divRow} style={{marginLeft: 'auto'}}>
                    {(props.filled && !props.disabled && !props.disableEdit) &&
                        <Typography variant="body2" onClick={props.onOpen} className={classes.editButton}>
                            {t('update')}
                        </Typography>
                    }

                    {(isMultiple && isLoadingIsHistory) &&
                        <AgyleLoadingSpinner disableText spinnerSize={14} className={classes.loadingSpinner}/>}

                    {isMultiple &&
                        <HistoryFormButton
                            disabled={props.disabled}
                            openFormPath={props.openFormPath}
                            isPatientAddData={props.isPatientAddData}
                            multiplePath={props.multiplePath}
                            customHistoryData={props.customHistoryData}
                            setLoading={setLoadingIsHistory}
                        />
                    }
                </div>
            </Grid>

            <Grid
                item
                xs={12}
                className={clsx(classes.seenDlg, props.filled && 'noPadding')}
                sx={{paddingBottom: props?.spacialRender && 0}}
                style={{
                    backgroundColor: props?.filledTextContainerBackground || '',
                    color: props?.filledTextContainerFontColor || '',
                }}
            >
                {!admitButton && renderCtx()}
                {!props.filled &&
                    <Grid item xs={12} sx={{px: 1}}>
                        {!props.disableEdit &&
                            <AgyleButton
                                width="99%"
                                variant="outlined"
                                color="primary"
                                onClick={handleOpenForm}
                                startIcon={<AddIcon/>}
                                disabled={props.disabled}
                                className={clsx(classes.addButton, (props.disabled || props.disableEdit) && 'disabled')}
                            >
                                {t('Add Data')}
                            </AgyleButton>
                        }
                    </Grid>
                }
            </Grid>
        </Grid>
    );
};

export default MainScreenButton;
